import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowForward, Close } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { stopWheelNudge } from '../../utils/numberInput';
import DuplicateSessionConfirmDialog from '../DuplicateSessionConfirmDialog';
import { DuplicateEntry, DuplicateSessionInfo, dupKey, getDuplicateSessionError } from '../../utils/duplicateSession';
import {
  ACCENT,
  ACCENT_TINT,
  BAD,
  BrandLocation,
  Loading,
  LocationPicker,
  LocationProblem,
  LocationTabs,
  ProblemsPanel,
  ResultPanel,
  StepHeader,
  addDays,
  fmtDay,
  idOf,
  istDate,
  matchClass,
  messageOf,
  plural,
  problemsOf,
  toIst,
  todayKey,
  useBrandLocations,
  useLocationData,
} from './shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  IMPORT SESSIONS FROM ANOTHER LOCATION
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   1  From     a location and a date range (up to 31 days), optionally moved
 *               forward by whole weeks — "copy this week into next week"
 *   2  To       one or more other locations
 *   3  Adjust   per destination: which of ITS classes each source class
 *               becomes (or skip it), then every session editable — date,
 *               time, instructor, slots, price, trial price
 *
 * Sessions need the destination's own class; nothing is created against the
 * source location's class.
 */

type Row = {
  include: boolean;
  dateKey: string;
  time: string;
  instructor: string; // '' = the class's instructor
  slots: string;
  price: string; // '' = the class's price
  isTrialEnabled: boolean;
  trialPrice: string;
};

const nowKey = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 16);
const MAX_DAYS = 31;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
  anchorStudioId?: string | null;
  preselectTarget?: string | null;
}

const ImportSessionsDialog: React.FC<Props> = ({ open, onClose, onCreated, anchorStudioId, preselectTarget }) => {
  const { creatable, readable, loading: locLoading, error: locError } = useBrandLocations(anchorStudioId);
  const data = useLocationData();
  const { load } = data;

  const [step, setStep] = useState(0);
  const [source, setSource] = useState('');
  const [from, setFrom] = useState(todayKey());
  const [to, setTo] = useState(addDays(todayKey(), 6));
  const [weeks, setWeeks] = useState(0);
  const [srcSessions, setSrcSessions] = useState<any[] | null>(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [targets, setTargets] = useState<string[]>([]);
  const [tab, setTab] = useState('');
  const [mapping, setMapping] = useState<Record<string, Record<string, string>>>({});
  const [rows, setRows] = useState<Record<string, Record<string, Row>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<LocationProblem[] | null>(null);
  const [dup, setDup] = useState<DuplicateSessionInfo | null>(null);
  const [result, setResult] = useState<{ created: any[]; failed: any[] } | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0); setSource(''); setFrom(todayKey()); setTo(addDays(todayKey(), 6)); setWeeks(0);
    setSrcSessions(null); setPicked([]); setTargets(preselectTarget ? [preselectTarget] : []); setTab('');
    setMapping({}); setRows({}); setSaving(false); setError(null); setProblems(null); setDup(null); setResult(null);
  }, [open, preselectTarget]);

  useEffect(() => { if (targets.length) load(targets); }, [targets, load]);

  const span = Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000) + 1;
  const rangeError = !from || !to ? 'Choose both dates.' : span < 1 ? 'The end date is before the start date.' : span > MAX_DAYS ? `Choose at most ${MAX_DAYS} days.` : null;

  // Load the source schedule for the range.
  useEffect(() => {
    if (!source || rangeError) { setSrcSessions(null); return; }
    let live = true;
    setLoadingSessions(true);
    apiService
      .getStudioSessions(source, { startDate: from, endDate: to }, source)
      .then((res: any) => {
        if (!live) return;
        const list = (res?.sessions ?? [])
          .filter((s: any) => idOf(s.studio) === source && !s.isManualCheckin)
          .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setSrcSessions(list);
        setPicked(list.map((s: any) => s._id));
      })
      .catch((e: any) => live && setError(messageOf(e, 'Could not load the sessions.')))
      .finally(() => live && setLoadingSessions(false));
    return () => { live = false; };
  }, [source, from, to, rangeError]);

  const srcName = readable.find((l) => l._id === source)?.name ?? '';
  const chosenSessions = (srcSessions ?? []).filter((s) => picked.includes(s._id));
  const srcClasses = useMemo(() => {
    const m = new Map<string, any>();
    chosenSessions.forEach((s) => { const c = s.class; if (c && !m.has(idOf(c))) m.set(idOf(c), typeof c === 'object' ? c : { _id: c, className: s.className }); });
    return Array.from(m.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked.join(','), srcSessions]);
  const targetLocs: BrandLocation[] = creatable.filter((l) => targets.includes(l._id) && l._id !== source);
  const targetsReady = targetLocs.every((l) => data.get(l._id));

  // Build mappings and rows for each destination once its data is in.
  useEffect(() => {
    if (step !== 2 || !targetsReady) return;
    setMapping((prev) => {
      const next = { ...prev };
      targetLocs.forEach((l) => {
        const ld = data.get(l._id)!;
        const cur = { ...(next[l._id] ?? {}) };
        srcClasses.forEach((c) => { if (!(c._id in cur)) cur[c._id] = matchClass(c, ld.classes)?._id ?? ''; });
        next[l._id] = cur;
      });
      return next;
    });
    setRows((prev) => {
      const next = { ...prev };
      targetLocs.forEach((l) => {
        const ld = data.get(l._id)!;
        const cur = { ...(next[l._id] ?? {}) };
        chosenSessions.forEach((s) => {
          if (cur[s._id]) return;
          const { dateKey, time } = toIst(s.date);
          const insId = idOf(s.instructor);
          const classIns = idOf(s.class?.instructor);
          const teaches = ld.instructors.some((i: any) => i._id === insId);
          const shifted = addDays(dateKey, weeks * 7);
          cur[s._id] = {
            include: `${shifted}T${time}` > nowKey(),
            dateKey: shifted,
            time,
            // Keep a cover instructor only if they teach here; otherwise the class's own.
            instructor: insId && insId !== classIns && teaches ? insId : '',
            slots: String(s.slots ?? ''),
            price: '',
            isTrialEnabled: !!s.isTrialEnabled,
            trialPrice: String(s.trialPrice ?? ''),
          };
        });
        next[l._id] = cur;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, targetsReady, targets.join(','), picked.join(','), weeks]);

  const updRow = (sid: string, id: string, patch: Partial<Row>) => {
    setRows((r) => ({ ...r, [sid]: { ...r[sid], [id]: { ...r[sid][id], ...patch } } }));
    setProblems(null);
  };
  const setMap = (sid: string, srcClass: string, target: string) => {
    setMapping((m) => ({ ...m, [sid]: { ...m[sid], [srcClass]: target } }));
    setProblems(null);
  };

  const targetClassFor = (sid: string, s: any) => {
    const id = mapping[sid]?.[idOf(s.class)];
    return id ? data.get(sid)?.classes.find((c: any) => c._id === id) ?? null : null;
  };
  const live = (sid: string) => chosenSessions.filter((s) => rows[sid]?.[s._id]?.include && targetClassFor(sid, s));
  const rowIssue = (sid: string, s: any): string | null => {
    const r = rows[sid]?.[s._id];
    if (!r?.include) return null;
    const cls = targetClassFor(sid, s);
    if (!cls) return null; // skipped via mapping
    if (!r.dateKey || !r.time) return 'Set the date and time';
    if (`${r.dateKey}T${r.time}` <= nowKey()) return 'Already passed';
    if (!cls.seatLayout && !(parseInt(r.slots, 10) >= 1)) return 'Set the slots';
    if (r.price !== '' && Number(r.price) < 0) return 'Price cannot be negative';
    if (r.isTrialEnabled && (r.trialPrice === '' || Number(r.trialPrice) < 0)) return 'Set the trial price';
    return null;
  };
  const issueCount = Object.fromEntries(targetLocs.map((l) => [l._id, chosenSessions.filter((s) => rowIssue(l._id, s)).length]));
  const totalIssues = targetLocs.reduce((n, l) => n + issueCount[l._id], 0);
  const total = targetLocs.reduce((n, l) => n + live(l._id).length, 0);

  const payloadFor = (sid: string) =>
    live(sid).map((s) => {
      const r = rows[sid][s._id];
      const cls = targetClassFor(sid, s);
      return {
        class: cls._id,
        date: istDate(r.dateKey, r.time),
        slots: parseInt(r.slots, 10) || 0,
        isLive: s.isLive !== false,
        onlyForFemales: !!s.onlyForFemales,
        isTrialEnabled: r.isTrialEnabled,
        trialPrice: r.isTrialEnabled ? parseFloat(r.trialPrice) || 0 : 0,
        ...(r.price !== '' ? { price: parseFloat(r.price) } : {}),
        ...(r.instructor ? { instructor: r.instructor } : {}),
      };
    });

  const send = async (allowDuplicate: boolean, skip: DuplicateEntry[] = []) => {
    setSaving(true); setError(null); setProblems(null);
    const skipKeys = new Set(skip.map((d) => dupKey(d.classId, d.date)));
    const locations = targetLocs
      .map((l) => ({ studioId: l._id, sessions: payloadFor(l._id).filter((p) => !skipKeys.has(dupKey(p.class, p.date))) }))
      .filter((l) => l.sessions.length);
    if (!locations.length) { setSaving(false); setDup(null); return; }
    try {
      const out: any = await apiService.createSessionsAtLocations({ locations, allowDuplicate });
      setDup(null);
      setResult(out);
      if (!out.failed?.length) onCreated(`${plural(out.total, 'session')} imported from ${srcName}`);
    } catch (e: any) {
      const d = getDuplicateSessionError(e);
      const p = problemsOf(e);
      if (d) setDup(d);
      else if (p) { setDup(null); setProblems(p); setTab(p[0].studioId); }
      else { setDup(null); setError(messageOf(e, 'The sessions could not be imported.')); }
    } finally {
      setSaving(false);
    }
  };

  // Group the source schedule by day for step 1.
  const byDay = useMemo(() => {
    const m = new Map<string, any[]>();
    (srcSessions ?? []).forEach((s) => { const k = toIst(s.date).dateKey; m.set(k, [...(m.get(k) ?? []), s]); });
    return Array.from(m.entries());
  }, [srcSessions]);
  const toggle = (ids: string[], on: boolean) => setPicked((p) => (on ? Array.from(new Set([...p, ...ids])) : p.filter((x) => !ids.includes(x))));

  const renderTarget = (sid: string) => {
    const ld = data.get(sid);
    if (!ld || !rows[sid] || !mapping[sid]) return <Loading label="Loading this location's classes and instructors…" />;
    const serverIssues = (problems ?? []).filter((p) => p.studioId === sid);
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {serverIssues.length > 0 && <Alert severity="error">{serverIssues.map((p, i) => <div key={i}>{p.message}</div>)}</Alert>}

        <Box p={2} borderRadius={2} bgcolor="#fafafa" border="1px solid #eee">
          <Typography fontWeight={700} mb={0.5}>Classes</Typography>
          <Typography variant="body2" color="text.secondary" mb={1.5}>Sessions use this location's own classes. Choose which class each one becomes here, or skip it.</Typography>
          <Box display="flex" flexDirection="column" gap={1.25}>
            {srcClasses.map((c) => {
              const v = mapping[sid][c._id] ?? '';
              return (
                <Box key={c._id} display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <Typography sx={{ minWidth: 160, flex: '1 1 160px' }} fontWeight={600}>{c.className}</Typography>
                  <ArrowForward fontSize="small" color="action" />
                  <FormControl size="small" sx={{ flex: '2 1 220px' }} error={!v}>
                    <Select value={v} displayEmpty onChange={(e) => setMap(sid, c._id, e.target.value)}>
                      <MenuItem value=""><em>Skip these sessions</em></MenuItem>
                      {ld.classes.map((t: any) => <MenuItem key={t._id} value={t._id}>{t.className} · ₹{t.price}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {!v && <Typography variant="caption" color="warning.main">No match here, so these are skipped</Typography>}
                </Box>
              );
            })}
          </Box>
        </Box>

        <TableContainer sx={{ border: '1px solid #eee', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa', whiteSpace: 'nowrap' } }}>
                <TableCell padding="checkbox" />
                <TableCell>Date</TableCell><TableCell>Time</TableCell><TableCell>Class here</TableCell>
                <TableCell>Instructor</TableCell><TableCell>Slots</TableCell><TableCell>Price</TableCell><TableCell>Trial</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chosenSessions.map((s) => {
                const r = rows[sid][s._id];
                if (!r) return null;
                const cls = targetClassFor(sid, s);
                const issue = rowIssue(sid, s);
                const off = !cls || !r.include;
                return (
                  <TableRow key={s._id} sx={{ opacity: off ? 0.5 : 1, bgcolor: issue ? '#fdecea' : undefined }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={r.include && !!cls} disabled={!cls} onChange={(e) => updRow(sid, s._id, { include: e.target.checked })}
                        sx={{ '&.Mui-checked': { color: ACCENT } }} inputProps={{ 'aria-label': 'Include' }} />
                    </TableCell>
                    <TableCell>
                      <TextField type="date" size="small" value={r.dateKey} disabled={off} onChange={(e) => updRow(sid, s._id, { dateKey: e.target.value })} sx={{ width: 150 }} />
                      {issue && <Typography variant="caption" color={BAD} display="block">{issue}</Typography>}
                    </TableCell>
                    <TableCell>
                      <TextField type="time" size="small" value={r.time} disabled={off} onChange={(e) => updRow(sid, s._id, { time: e.target.value })} sx={{ width: 115 }} inputProps={{ step: 300 }} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{cls ? cls.className : <Typography variant="body2" color="text.secondary">Skipped</Typography>}</TableCell>
                    <TableCell>
                      <Select size="small" displayEmpty value={ld.instructors.some((i: any) => i._id === r.instructor) ? r.instructor : ''} disabled={off}
                        onChange={(e) => updRow(sid, s._id, { instructor: e.target.value })} sx={{ minWidth: 150, ...(r.instructor ? { bgcolor: ACCENT_TINT } : {}) }}>
                        <MenuItem value=""><em>{cls?.instructor?.name ? `${cls.instructor.name} (class)` : 'Class instructor'}</em></MenuItem>
                        {ld.instructors.map((i: any) => <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField type="number" size="small" value={r.slots} disabled={off} onWheel={stopWheelNudge}
                        placeholder={cls?.seatLayout ? 'room' : ''} onChange={(e) => updRow(sid, s._id, { slots: e.target.value })} sx={{ width: 80 }} />
                    </TableCell>
                    <TableCell>
                      <TextField type="number" size="small" value={r.price} disabled={off} onWheel={stopWheelNudge}
                        placeholder={cls ? String(cls.price) : ''} onChange={(e) => updRow(sid, s._id, { price: e.target.value })} sx={{ width: 95 }} />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Checkbox size="small" checked={r.isTrialEnabled} disabled={off} onChange={(e) => updRow(sid, s._id, { isTrialEnabled: e.target.checked })}
                          sx={{ p: 0.5, '&.Mui-checked': { color: ACCENT } }} inputProps={{ 'aria-label': 'Trial session' }} />
                        {r.isTrialEnabled && (
                          <TextField type="number" size="small" value={r.trialPrice} disabled={off} onWheel={stopWheelNudge} placeholder="₹"
                            onChange={(e) => updRow(sid, s._id, { trialPrice: e.target.value })} sx={{ width: 80 }} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const steps = ['From', 'To', 'Adjust & import'];
  const weekLabel = (w: number) => (w === 0 ? 'Same dates' : `${w} week${w === 1 ? '' : 's'} later (${fmtDay(addDays(from, w * 7))} – ${fmtDay(addDays(to, w * 7))})`);

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" fontWeight={700}>Import sessions</Typography>
          <Typography variant="body2" color="text.secondary">Copy a schedule from one location to others, and adjust it before it's created</Typography>
          <IconButton onClick={onClose} disabled={saving} sx={{ position: 'absolute', right: 12, top: 12 }} aria-label="Close"><Close /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: 460 }}>
          {result ? (
            <ResultPanel created={result.created.map((c: any) => ({ location: c.location, label: `${plural(c.count, 'session')} imported` }))} failed={result.failed} />
          ) : (
            <>
              <StepHeader steps={steps} active={step} />
              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
              {problems && <ProblemsPanel problems={problems} onGo={setTab} />}

              {step === 0 && (
                locLoading ? <Loading label="Loading your locations…" /> : locError ? <Alert severity="error">{locError}</Alert> : (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <FormControl size="small" sx={{ flex: '2 1 220px' }}>
                        <InputLabel>Import from *</InputLabel>
                        <Select value={source} label="Import from *" onChange={(e) => setSource(e.target.value)}>
                          {readable.map((l) => <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField label="From" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} />
                      <TextField label="To" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }}
                        error={!!rangeError} helperText={rangeError ?? ' '} />
                      <FormControl size="small" sx={{ flex: '2 1 240px' }}>
                        <InputLabel>Create on</InputLabel>
                        <Select value={weeks} label="Create on" onChange={(e) => setWeeks(Number(e.target.value))}>
                          {[0, 1, 2, 3, 4, 8].map((w) => <MenuItem key={w} value={w}>{weekLabel(w)}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                    {source && loadingSessions && <Loading label={`Loading ${srcName}'s sessions…`} />}
                    {source && !loadingSessions && srcSessions && (
                      srcSessions.length === 0 ? <Alert severity="info">{srcName} has no sessions between these dates.</Alert> : (
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="body2" color="text.secondary">{plural(picked.length, 'session')} of {srcSessions.length} selected</Typography>
                            <Button size="small" onClick={() => toggle(srcSessions.map((s) => s._id), picked.length !== srcSessions.length)}>
                              {picked.length === srcSessions.length ? 'Clear all' : 'Select all'}
                            </Button>
                          </Box>
                          <Box display="flex" flexDirection="column" gap={1.25}>
                            {byDay.map(([day, list]) => {
                              const ids = list.map((s) => s._id);
                              const allOn = ids.every((id) => picked.includes(id));
                              return (
                                <Box key={day} p={1.25} borderRadius={2} border="1px solid #eee">
                                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                                    <Checkbox size="small" checked={allOn} indeterminate={!allOn && ids.some((id) => picked.includes(id))}
                                      onChange={(e) => toggle(ids, e.target.checked)} sx={{ p: 0.5, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: ACCENT } }} />
                                    <Typography fontWeight={700}>{fmtDay(day)}</Typography>
                                    {weeks > 0 && <Chip size="small" label={`→ ${fmtDay(addDays(day, weeks * 7))}`} sx={{ bgcolor: ACCENT_TINT }} />}
                                  </Box>
                                  <Box display="flex" gap={0.75} flexWrap="wrap" pl={4}>
                                    {list.map((s) => {
                                      const on = picked.includes(s._id);
                                      return (
                                        <Chip key={s._id} clickable onClick={() => toggle([s._id], !on)} variant={on ? 'filled' : 'outlined'}
                                          sx={on ? { bgcolor: ACCENT_TINT, border: `1px solid ${ACCENT}` } : undefined}
                                          label={`${toIst(s.date).time} · ${s.className ?? s.class?.className} · ${s.instructor?.name ?? ''}`} />
                                      );
                                    })}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )
                    )}
                  </Box>
                )
              )}

              {step === 1 && (
                <LocationPicker locations={creatable} selected={targets} onChange={setTargets} disabledIds={[source]} disabledReason="This is where you're importing from"
                  helper={<Typography variant="body2" color="text.secondary">Next you match each class to the location's own class and can change any session before importing.</Typography>} />
              )}

              {step === 2 && (
                <>
                  <LocationTabs value={tab || targetLocs[0]?._id || ''} onChange={setTab} locations={targetLocs} issues={issueCount} withAllTab={false} />
                  {!targetsReady ? <Loading label="Loading classes and instructors…" /> : renderTarget(tab || targetLocs[0]?._id)}
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          {result ? (
            <Button variant="contained" onClick={onClose} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } }}>Done</Button>
          ) : (
            <>
              <Box flex={1}>
                {step === 2 && (
                  <Typography variant="body2" color={totalIssues ? 'error' : 'text.secondary'}>
                    {totalIssues ? `${plural(totalIssues, 'session')} need fixing` : `${plural(total, 'session')} will be imported`}
                  </Typography>
                )}
              </Box>
              {step > 0 && <Button onClick={() => setStep(step - 1)} disabled={saving}>Back</Button>}
              {step === 0 && <Button variant="contained" disabled={!picked.length || !!rangeError} onClick={() => { setTargets(targets.filter((t) => t !== source)); setRows({}); setMapping({}); setStep(1); }} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } }}>Next</Button>}
              {step === 1 && <Button variant="contained" disabled={!targetLocs.length} onClick={() => { setTab(targetLocs[0]._id); setStep(2); }} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } }}>Next</Button>}
              {step === 2 && (
                <Button variant="contained" disabled={saving || totalIssues > 0 || !total || !targetsReady} onClick={() => send(false)}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } }}>
                  {saving ? 'Importing…' : `Import ${plural(total, 'session')}`}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <DuplicateSessionConfirmDialog open={!!dup} info={dup} loading={saving} totalRequested={total} onCancel={() => setDup(null)} onConfirm={(skip) => send(true, skip)} />
    </>
  );
};

export default ImportSessionsDialog;
